/**
 * React Hook for Report Upload functionality
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadReport, ReportUploadData, ReportUploadResponse } from '@/integrations/supabase/reportUploadService';
import { useToast } from '@/hooks/use-toast';

export interface UseReportUploadReturn {
  uploadReport: (data: ReportUploadData) => Promise<ReportUploadResponse>;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  reset: () => void;
}

export function useReportUpload(): UseReportUploadReturn {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: ReportUploadData) => {
      setError(null);
      setUploadProgress(0);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      try {
        const result = await uploadReport(data);
        clearInterval(progressInterval);
        setUploadProgress(100);

        if (result.success) {
          toast({
            title: "Upload thành công",
            description: `File báo cáo "${data.file.name}" đã được upload và lưu trữ thành công.`,
          });

          // Invalidate reports queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['reports'] });
          queryClient.invalidateQueries({ queryKey: ['supabase-reports'] });
        } else {
          throw new Error(result.error || 'Upload failed');
        }

        return result;
      } catch (err) {
        clearInterval(progressInterval);
        setUploadProgress(0);
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
        
        toast({
          title: "Upload thất bại",
          description: errorMessage,
          variant: "destructive",
        });
        
        throw err;
      }
    },
    onSuccess: () => {
      // Reset progress after a delay
      setTimeout(() => {
        setUploadProgress(0);
      }, 2000);
    },
  });

  const reset = () => {
    setUploadProgress(0);
    setError(null);
    mutation.reset();
  };

  return {
    uploadReport: mutation.mutateAsync,
    isUploading: mutation.isPending,
    uploadProgress,
    error,
    reset,
  };
}




